import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { CatalogService, MarketplaceStore } from '../../features/catalog/catalog.service';

interface StoreOption {
  id: string;
  label: string;
}

const SELECTED_STORE_SESSION_KEY = 'ain.marketplace.selectedStoreId';

@Component({
  standalone: true,
  selector: 'ain-marketplace-store-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MarketplaceStoreSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="id"
      [placeholder]="t(placeholderKey)"
      [showClear]="showClear"
      [filter]="showSearch"
      filterBy="label,id"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`.selector { width: 100%; }`]
})
export class MarketplaceStoreSelectorComponent implements ControlValueAccessor {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() placeholderKey = 'catalog.storefront.selectStore';
  @Input() showSearch = true;
  @Input() showClear = true;
  @Input() appendTo: 'body' | 'self' = 'body';
  @Input() autoSelectDefault = true;

  readonly loading = signal(false);
  readonly options = signal<StoreOption[]>([]);
  readonly value = signal<string | null>(null);
  disabled = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.value.set(value?.trim() || null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.persistSelection(value);
    this.onChange(value);
    this.onTouched();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private load(): void {
    this.loading.set(true);
    this.service.listMarketplaceStores().subscribe({
      next: rows => {
        this.options.set(rows.map(row => this.toOption(row)));
        this.tryAutoSelect(rows);
        this.loading.set(false);
      },
      error: () => {
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }

  private tryAutoSelect(stores: MarketplaceStore[]): void {
    if (!this.autoSelectDefault || this.value() || stores.length === 0) {
      return;
    }

    const resolved = this.resolveDefaultStoreId(stores);
    if (resolved) {
      this.onValueChange(resolved);
    }
  }

  private resolveDefaultStoreId(stores: MarketplaceStore[]): string | null {
    const persisted = sessionStorage.getItem(SELECTED_STORE_SESSION_KEY)?.trim();
    if (persisted && stores.some(store => store.id === persisted)) {
      return persisted;
    }

    const defaultStore = stores.find(store => store.storeKey.trim().toLowerCase() === 'default');
    if (defaultStore) {
      return defaultStore.id;
    }

    if (stores.length === 1) {
      return stores[0].id;
    }

    return null;
  }

  private persistSelection(value: string | null): void {
    if (value?.trim()) {
      sessionStorage.setItem(SELECTED_STORE_SESSION_KEY, value.trim());
      return;
    }

    sessionStorage.removeItem(SELECTED_STORE_SESSION_KEY);
  }

  private toOption(row: MarketplaceStore): StoreOption {
    return { id: row.id, label: `${row.storeKey} - ${row.name}` };
  }
}
